import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarClock, CalendarCheck2, Briefcase, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { PaginaKop } from '../components/ZijMenu';
import { ProfessionalDashboardSchil } from '../components/ProfessionalDashboardSchil';
import { SignaalVenster } from '../components/professionalDashboard/SignaalVenster';
import { VandaagLijst } from '../components/professionalDashboard/VandaagLijst';
import { AfspraakVerzoekKaart } from '../components/professionalDashboard/AfspraakVerzoekKaart';
import { ActieKaart } from '../components/professionalDashboard/ActieKaart';
import { TodoBalk } from '../components/professionalDashboard/TodoBalk';
import { TariefContractjaarKaart } from '../components/tariefContractjaar/TariefContractjaarKaart';
import { Schakelbord } from '../components/professionalDashboard/Schakelbord';
import { MarktplaatsBlok } from '../components/professionalDashboard/MarktplaatsBlok';
import { HulplijnWachtrijLijst, useHulplijnWachtrij } from '../components/professionalDashboard/HulplijnWachtrij';
import { VeiligeGesprekkenNode } from '../components/veiligeHaven/VeiligeGesprekken';
import { dashboardApi, DASHBOARD_KEY, type DashboardVenster, type DashboardVensterId } from '../services/professionalDashboardApi';
import type { WerkplekSignal } from '../services/professionalWerkplekApi';
import { initSocket } from '../services/socketService';
import { ContractAddendumPopup } from '../components/ContractAddendumPopup';

const ICOON: Record<DashboardVensterId, React.ComponentType<{ className?: string }>> = {
  vandaag: CalendarClock,
  boekingen: CalendarCheck2,
  inkoop: Briefcase,
  administratie: ShieldCheck,
};
const VOLGORDE: DashboardVensterId[] = ['vandaag', 'boekingen', 'inkoop', 'administratie'];
const ZICHTBAAR = 4;

/**
 * Centraal dashboard van de professional (Onderdeel 55):
 *   1. to-do-balk + de vier signaalvensters (altijd zichtbaar; leeg = Zero-State),
 *   2. daaronder het schakelbord (Job-Radar en communicatiekanalen),
 *   3. onderaan de marktplaats.
 * Spoed-terugbelverzoeken van de Directe Hulplijn staan bovenin Venster 1 tot
 * iemand uit de pool ze claimt; rechtsonder zweeft de chat-console (casusnummers).
 */
export const ProfessionalDashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation('professionalDashboard');
  const { t: tPages } = useTranslation('pages');
  const queryClient = useQueryClient();
  const [uitgeklapt, setUitgeklapt] = useState<DashboardVensterId | null>(null);
  const hulplijn = useHulplijnWachtrij();
  const hulplijnAantal = (hulplijn.data?.open.length ?? 0) + (hulplijn.data?.geclaimd.length ?? 0);

  const { data, isLoading, isError } = useQuery({
    queryKey: DASHBOARD_KEY,
    queryFn: dashboardApi.vensters,
    // Afgeleide signalen hebben geen socket-event; pollen houdt de tellers actueel.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const socket = initSocket();
    if (!socket) return;
    const ververs = () => queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY });
    socket.on('new_notification', ververs);
    return () => {
      socket.off('new_notification', ververs);
    };
  }, [queryClient]);

  const uur = new Date().getHours();
  const groet = tPages(uur < 12 ? 'dashboard.greeting.morning' : uur < 18 ? 'dashboard.greeting.afternoon' : 'dashboard.greeting.evening');
  const venster = (id: DashboardVensterId) => data?.find((v) => v.id === id);

  const inhoud = (v: DashboardVenster) => {
    if (v.id === 'vandaag') return v.items.length > 0 ? <VandaagLijst items={v.items} /> : null;
    const alles = uitgeklapt === v.id;
    const items: WerkplekSignal[] = alles ? v.items : v.items.slice(0, ZICHTBAAR);
    return (
      <div className="space-y-2">
        {items.map((s) =>
          s.key === 'afspraak_goedkeuring' ? <AfspraakVerzoekKaart key={s.id} signaal={s} /> : <ActieKaart key={s.id} signaal={s} />,
        )}
        {v.items.length > ZICHTBAAR && (
          <button
            type="button"
            onClick={() => setUitgeklapt(alles ? null : v.id)}
            className="px-1 text-xs font-semibold text-navy-700 hover:text-navy-900"
          >
            {alles ? t('venster.minder') : t('venster.meer', { count: v.items.length })}
          </button>
        )}
      </div>
    );
  };

  return (
    <ProfessionalDashboardSchil>
      <div className="px-4 sm:px-6 lg:px-8 pt-8">
        <PaginaKop label={t('titel')} titel={`${groet}, ${user?.first_name ?? ''}`.trim()} ondertitel={t('ondertitel')} />
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <TodoBalk />

        <TariefContractjaarKaart />

        {isError && <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{t('laadFout')}</p>}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {VOLGORDE.map((id) => {
            const v = venster(id);
            return (
              <SignaalVenster
                key={id}
                id={id}
                icon={ICOON[id]}
                badge={(v?.badge_counter ?? 0) + (id === 'vandaag' ? hulplijnAantal : 0)}
                laden={isLoading}
              >
                {id === 'vandaag' && hulplijn.data && <HulplijnWachtrijLijst data={hulplijn.data} />}
                {v && inhoud(v)}
              </SignaalVenster>
            );
          })}
        </div>

        <Schakelbord />

        <MarktplaatsBlok />
      </div>

      <VeiligeGesprekkenNode rol="professional" />
      <ContractAddendumPopup context="dashboard" />
    </ProfessionalDashboardSchil>
  );
};
